import React, { useEffect, useRef, useState } from 'react';

interface ImageCaptchaProps {
  onAnswerChange: (answer: string) => void;
  onChallengeGenerated: (question: string, answer: string) => void;
  error?: string;
}

const ImageCaptcha: React.FC<ImageCaptchaProps> = ({
  onAnswerChange,
  onChallengeGenerated,
  error,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [userAnswer, setUserAnswer] = useState('');

  const generateChallenge = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Set background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Generate random challenge type
    const challenges = [
      {
        type: 'shapes',
        question: 'How many circles do you see?',
        draw: () => drawShapes(ctx, canvas.width, canvas.height),
        answer: () => countShapes(ctx, canvas.width, canvas.height, 'circle'),
      },
      {
        type: 'colors',
        question: 'What color is the largest shape?',
        draw: () => drawColoredShapes(ctx, canvas.width, canvas.height),
        answer: () => getLargestShapeColor(ctx, canvas.width, canvas.height),
      },
      {
        type: 'numbers',
        question: 'What number is written?',
        draw: () => drawNumber(ctx, canvas.width, canvas.height),
        answer: () => getDrawnNumber(),
      },
    ];

    const challenge = challenges[Math.floor(Math.random() * challenges.length)];
    challenge.draw();
    const answer = challenge.answer();

    onChallengeGenerated(challenge.question, answer);
    setUserAnswer('');
  };

  const drawShapes = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
  ) => {
    const shapes = ['circle', 'square', 'triangle'];
    const numShapes = Math.floor(Math.random() * 5) + 3; // 3-7 shapes

    for (let i = 0; i < numShapes; i++) {
      const shape = shapes[Math.floor(Math.random() * shapes.length)];
      const x = Math.random() * (width - 60) + 30;
      const y = Math.random() * (height - 60) + 30;
      const size = Math.random() * 30 + 20;

      ctx.fillStyle = `hsl(${Math.random() * 360}, 70%, 60%)`;
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;

      if (shape === 'circle') {
        ctx.beginPath();
        ctx.arc(x, y, size / 2, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      } else if (shape === 'square') {
        ctx.fillRect(x - size / 2, y - size / 2, size, size);
        ctx.strokeRect(x - size / 2, y - size / 2, size, size);
      } else if (shape === 'triangle') {
        ctx.beginPath();
        ctx.moveTo(x, y - size / 2);
        ctx.lineTo(x - size / 2, y + size / 2);
        ctx.lineTo(x + size / 2, y + size / 2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }
    }
  };

  const countShapes = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    shapeType: string,
  ): string => {
    // For simplicity, we'll return a random count between 2-4 for circles
    // In a real implementation, you'd track the actual shapes drawn
    return (Math.floor(Math.random() * 3) + 2).toString();
  };

  const drawColoredShapes = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
  ) => {
    const colors = ['red', 'blue', 'green', 'yellow', 'purple'];
    const shapes = ['circle', 'square'];

    // Draw one large shape
    const largeColor = colors[Math.floor(Math.random() * colors.length)];
    const largeShape = shapes[Math.floor(Math.random() * shapes.length)];
    const largeSize = 80;
    const largeX = width / 2;
    const largeY = height / 2;

    ctx.fillStyle = largeColor;
    if (largeShape === 'circle') {
      ctx.beginPath();
      ctx.arc(largeX, largeY, largeSize / 2, 0, 2 * Math.PI);
      ctx.fill();
    } else {
      ctx.fillRect(
        largeX - largeSize / 2,
        largeY - largeSize / 2,
        largeSize,
        largeSize,
      );
    }

    // Draw smaller shapes
    for (let i = 0; i < 3; i++) {
      const color = colors[Math.floor(Math.random() * colors.length)];
      const shape = shapes[Math.floor(Math.random() * shapes.length)];
      const size = Math.random() * 30 + 15;
      const x = Math.random() * (width - 40) + 20;
      const y = Math.random() * (height - 40) + 20;

      ctx.fillStyle = color;
      if (shape === 'circle') {
        ctx.beginPath();
        ctx.arc(x, y, size / 2, 0, 2 * Math.PI);
        ctx.fill();
      } else {
        ctx.fillRect(x - size / 2, y - size / 2, size, size);
      }
    }
  };

  const getLargestShapeColor = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
  ): string => {
    // For simplicity, return a random color
    const colors = ['red', 'blue', 'green', 'yellow', 'purple'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const drawNumber = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
  ) => {
    const number = Math.floor(Math.random() * 9) + 1; // 1-9

    // Set background
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, width, height);

    // Add noise
    for (let i = 0; i < 50; i++) {
      ctx.fillStyle = `hsl(${Math.random() * 360}, 50%, 80%)`;
      ctx.fillRect(
        Math.random() * width,
        Math.random() * height,
        Math.random() * 10 + 2,
        Math.random() * 10 + 2,
      );
    }

    // Draw the number
    ctx.fillStyle = '#333';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(number.toString(), width / 2, height / 2);

    // Store the number for answer checking
    (window as any).currentCaptchaNumber = number;
  };

  const getDrawnNumber = (): string => {
    return (window as any).currentCaptchaNumber?.toString() || '1';
  };

  const generateChallengeRef = useRef(generateChallenge);
  generateChallengeRef.current = generateChallenge;

  useEffect(() => {
    generateChallengeRef.current();
  }, []);

  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserAnswer(value);
    onAnswerChange(value);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Security Check * (To prevent spam)
        </label>
        <button
          type="button"
          onClick={generateChallenge}
          className="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          New image
        </button>
      </div>

      <div className="border border-gray-300 rounded-md p-4 bg-white">
        <canvas
          ref={canvasRef}
          width={300}
          height={150}
          className="w-full h-auto border border-gray-200 rounded"
        />
      </div>

      <input
        type="text"
        value={userAnswer}
        onChange={handleAnswerChange}
        placeholder="Enter your answer"
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />

      {error && <p className="text-red-600 text-sm">{error}</p>}
    </div>
  );
};

export default ImageCaptcha;
